import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type AdminPlaceFeedbackPage, type AdminPlaceFeedbackView, type AdminPlaceView, type PlaceFeedbackKindView, type PlaceFeedbackRejectReasonView } from '@vello/api-client';
import { Badge, Button, Checkbox, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, ListTable, Notice, RadioGroup, Row, SectionTitle, SegmentedControl, Select, Stack, Text, TextInput, type Column } from '@vello/web-theme';
import {
  FEEDBACK_KINDS,
  FEEDBACK_PAGE_SIZE,
  FEEDBACK_REJECT_REASONS,
  FEEDBACK_STATUS_FILTERS,
  acceptBodyOf,
  acceptCreatesPlace,
  acceptDraftIssue,
  acceptDraftOf,
  acceptNeedsOverride,
  draftPoint,
  feedbackActions,
  feedbackHeadline,
  feedbackTargetPlace,
  formatCoordinate,
  formatMarketInstant,
  placeFeedbackQuery,
  placeTimeZone,
  placesAuthority,
  placesFilterTimeZone,
  withAcceptPoint,
  type AcceptDraft,
  type FeedbackStatusFilter,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { DetailRow } from '../components/detail-row';
import { DrawerStack } from '../components/drawer-stack';
import { FormDialog } from '../components/form-dialog';
import { IdRef } from '../components/code-ref';
import { PlaceMap, usePlaceMapConfig, vocabularyCodes } from '../components/place-form';

/**
 * Operações › Endereços › Sugestões (D704, doc 28 gap G6): what riders suggested ("Sugerir um local") or corrected ("Corrigir este local") from the address search - a
 * review queue, pending first. Nothing a rider sends changes the base until an operator decides here:
 *
 *  - Aceitar: a new place enters the base with source "Sugestão de passageiro" (never verified by this alone; the operator may adjust the name, the address, the kind
 *    and the point - the point is required when the rider pinned none); a correction is applied to the base place (a closed place is hidden). A place an operator
 *    VERIFIED is protected: the form asks for the explicit "apply anyway" before the server accepts it;
 *  - Unir a um endereço: the suggestion is a place the base already has; the rider's name becomes another name of it;
 *  - Recusar: with one of the catalogued reasons (the operator picks one; none is preselected); the base does not change;
 *  - Marcar como resolvido: a rider's comment about the search ("Outros comentários") - it never reaches the base, so it is only handled or rejected.
 *
 * The rider is never named here. Every code is the central catalog's word; every change of the base is audited by the server.
 */

type Banner = { readonly tone: 'success' | 'danger'; readonly text: string };

export function PlaceFeedbackPanel({ onDecided }: { readonly onDecided?: () => void }): ReactElement {
  const { runtime, scope, grants, markets } = useAdmin();
  const { t, lang } = useT();
  const { v, explain, fail } = useVocab();
  const placeNames = usePlaceNames();
  const { toast } = useConsole();
  const authority = placesAuthority(grants);
  const current = scope.current;
  const scopeZone = placesFilterTimeZone(current, markets);
  const scopeKey = `${current.country ?? ''}|${current.region ?? ''}|${current.city ?? ''}`;

  const [status, setStatus] = useState<FeedbackStatusFilter>('pending');
  const [kind, setKind] = useState<PlaceFeedbackKindView | ''>('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<AdminPlaceFeedbackPage | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const readSeq = useRef(0);
  const refuse = useCallback((e: { readonly code: string; readonly reasonCode?: string | undefined }): string => explain(e) ?? t('places.error', { code: fail(e) }), [explain, fail, t]);

  useEffect(() => {
    setOffset(0);
  }, [scopeKey, status, kind]);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    const res = await runtime.placeFeedback.list(placeFeedbackQuery(current, status, kind, offset));
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setData(res.value);
      setError(undefined);
    } else setError(refuse(res.error));
  }, [runtime, current, status, kind, offset, refuse]);

  useEffect(() => {
    void load();
  }, [load]);

  const places: Readonly<Record<string, AdminPlaceView>> = data?.places ?? {};
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const when = (iso: string, marketId: string): string => formatMarketInstant(iso, placeTimeZone({ marketId }, scopeZone, markets), lang, 'datetime');
  const statusTone = (s: AdminPlaceFeedbackView['status']): 'warning' | 'success' | 'neutral' => (s === 'pending' ? 'warning' : s === 'rejected' ? 'neutral' : 'success');
  const drawerTitle = (f: AdminPlaceFeedbackView): string => t(f.kind === 'add' ? 'places.feedback.drawer.title.add' : f.kind === 'comment' ? 'places.feedback.drawer.title.comment' : 'places.feedback.drawer.title.fix');
  const kindLine = (f: AdminPlaceFeedbackView): string => (f.issue === null ? v('place.feedback_kind', f.kind) : `${v('place.feedback_kind', f.kind)} · ${v('place.feedback_issue', f.issue)}`);

  const columns: readonly Column<AdminPlaceFeedbackView>[] = [
    { key: 'received', header: t('places.feedback.col.received'), width: 'minmax(170px, 1fr)', render: (f) => <Text className="tnum">{when(f.createdAt, f.marketId)}</Text> },
    { key: 'city', header: t('places.feedback.col.city'), width: 'minmax(140px, 0.8fr)', render: (f) => <Text>{placeNames.market(f.marketId)}</Text> },
    { key: 'kind', header: t('places.feedback.col.kind'), width: 'minmax(200px, 1.2fr)', render: (f) => <Text>{kindLine(f)}</Text> },
    { key: 'suggestion', header: t('places.feedback.col.suggestion'), width: 'minmax(240px, 1.8fr)', render: (f) => <span title={feedbackHeadline(f)}>{feedbackHeadline(f)}</span> },
    {
      key: 'about',
      header: t('places.feedback.col.about'),
      width: 'minmax(200px, 1.2fr)',
      render: (f) => {
        const p = feedbackTargetPlace(f, places);
        return <Text tone={p === undefined ? 'secondary' : 'primary'}>{p !== undefined ? (p.name ?? p.formattedAddress) : f.targetLabel ?? '—'}</Text>;
      },
    },
    { key: 'status', header: t('places.feedback.col.status'), width: 'minmax(150px, 0.8fr)', render: (f) => <Badge tone={statusTone(f.status)}>{v('place.feedback_status', f.status)}</Badge> },
  ];

  // ---- the drawer -------------------------------------------------------------------------------------------------------------------------------

  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const open = openId === undefined ? undefined : rows.find((f) => f.id === openId);
  const openTarget = open === undefined ? undefined : feedbackTargetPlace(open, places);
  const mapMarket = open?.marketId ?? (current.city !== undefined && current.city !== '' ? current.city : undefined);
  const map = usePlaceMapConfig(mapMarket);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Banner | undefined>(undefined);
  const close = (): void => {
    setOpenId(undefined);
    setNotice(undefined);
  };
  const decided = async (message: string): Promise<void> => {
    toast({ message, tone: 'success' });
    close();
    await load();
    onDecided?.();
  };

  // accept
  const [accepting, setAccepting] = useState<AcceptDraft | undefined>(undefined);
  const kinds = vocabularyCodes('place.kind');
  const acceptIssue = open === undefined || accepting === undefined ? undefined : acceptDraftIssue(open, accepting, places);
  const runAccept = async (): Promise<void> => {
    if (open === undefined || accepting === undefined || acceptIssue !== undefined) return;
    setBusy(true);
    const res = await runtime.placeFeedback.accept(open.id, acceptBodyOf(open, accepting));
    setBusy(false);
    if (res.ok) {
      setAccepting(undefined);
      await decided(t(res.value.created ? 'places.feedback.done.created' : 'places.feedback.done.accepted'));
    } else setNotice({ tone: 'danger', text: refuse(res.error) });
  };

  // merge
  const [merging, setMerging] = useState(false);
  const [mergeQ, setMergeQ] = useState('');
  const [mergeHits, setMergeHits] = useState<readonly AdminPlaceView[] | undefined>(undefined);
  const [mergeInto, setMergeInto] = useState<string>('');
  const [mergeOverride, setMergeOverride] = useState(false);
  const searchMerge = async (): Promise<void> => {
    if (open === undefined || mergeQ.trim() === '') return;
    const res = await runtime.places.list({ country: open.countryCode, q: mergeQ.trim(), limit: 8 });
    setMergeHits(res.ok ? res.value.items : []);
  };
  const mergeTarget = mergeHits?.find((p) => p.id === mergeInto);
  const runMerge = async (): Promise<void> => {
    if (open === undefined || mergeInto === '') return;
    setBusy(true);
    const res = await runtime.placeFeedback.merge(open.id, mergeInto, mergeOverride);
    setBusy(false);
    if (res.ok) {
      setMerging(false);
      await decided(t('places.feedback.done.merged'));
    } else setNotice({ tone: 'danger', text: refuse(res.error) });
  };

  // reject: the dialog opens with no reason chosen; the operator picks one before confirming (the reason is decision data, never a default)
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState<PlaceFeedbackRejectReasonView | undefined>(undefined);
  const openReject = (): void => {
    setRejectReason(undefined);
    setRejectOpen(true);
  };
  const runReject = async (): Promise<void> => {
    if (open === undefined || rejectReason === undefined) return;
    setBusy(true);
    const res = await runtime.placeFeedback.reject(open.id, rejectReason);
    setBusy(false);
    if (res.ok) {
      setRejectOpen(false);
      await decided(t('places.feedback.done.rejected'));
    } else setNotice({ tone: 'danger', text: refuse(res.error) });
  };

  // handle: a comment about the search, read and acted on (nothing to adjust, nothing written into the base)
  const runHandle = async (): Promise<void> => {
    if (open === undefined) return;
    setBusy(true);
    const res = await runtime.placeFeedback.handle(open.id);
    setBusy(false);
    if (res.ok) await decided(t('places.feedback.done.handled'));
    else setNotice({ tone: 'danger', text: refuse(res.error) });
  };
  const actions = open === undefined ? undefined : feedbackActions(open);

  const acceptBody = open === undefined ? '' : open.kind === 'fix' && open.issue === 'closed' && open.targetPlaceId !== null ? t('places.feedback.accept.body.closed') : acceptCreatesPlace(open) ? t('places.feedback.accept.body.add') : t('places.feedback.accept.body.fix');
  const acceptPoint = accepting === undefined ? undefined : draftPoint(accepting.latitude, accepting.longitude);
  const showAcceptPoint = open !== undefined && (acceptCreatesPlace(open) ? open.issue !== 'closed' : open.issue === 'wrong_location' || open.issue === 'other');

  return (
    <Stack gap={4}>
      <Text role="label" tone="secondary">{t('places.feedback.subtitle')}</Text>
      {!authority.mayManage && <Notice tone="neutral">{t('places.feedback.readOnly')}</Notice>}
      {error !== undefined && <Notice tone="danger">{error}</Notice>}

      <Row gap={3} align="center" wrap>
        <SegmentedControl
          ariaLabel={t('places.feedback.filter.status')}
          value={status}
          onChange={setStatus}
          options={FEEDBACK_STATUS_FILTERS.map((s) => ({ id: s, label: s === 'all' ? t('places.feedback.filter.all') : v('place.feedback_status', s) }))}
        />
        <Select value={kind} aria-label={t('places.feedback.filter.kind')} onChange={(e) => setKind(e.target.value as PlaceFeedbackKindView | '')}>
          <option value="">{t('places.feedback.filter.anyKind')}</option>
          {FEEDBACK_KINDS.map((k) => (
            <option key={k} value={k}>{v('place.feedback_kind', k)}</option>
          ))}
        </Select>
        <Text role="caption" tone="secondary" className="tnum">{t(total === 1 ? 'places.feedback.totalOne' : 'places.feedback.total', { total })}</Text>
      </Row>

      <ListTable
        columns={columns}
        rows={rows}
        rowKey={(f) => f.id}
        onRowClick={(f) => {
          setOpenId(f.id);
          setNotice(undefined);
        }}
        {...(openId !== undefined ? { selectedKey: openId } : {})}
        loading={data === undefined && error === undefined}
        loadingLabel={t('ui.loading')}
        error={error !== undefined ? t('places.feedback.failed') : undefined}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('places.feedback.empty.title')} detail={t('places.feedback.empty.detail')} />}
        minWidth={1100}
        pagination={{
          from: total === 0 ? 0 : offset + 1,
          to: offset + rows.length,
          total,
          hasPrev: offset > 0,
          hasNext: offset + FEEDBACK_PAGE_SIZE < total,
          onPrev: () => setOffset((o) => Math.max(0, o - FEEDBACK_PAGE_SIZE)),
          onNext: () => setOffset((o) => o + FEEDBACK_PAGE_SIZE),
          prevLabel: t('dir.prev'),
          nextLabel: t('dir.next'),
          rangeLabel: t('dir.range', { from: String(total === 0 ? 0 : offset + 1), to: String(offset + rows.length), total: String(total) }),
        }}
      />

      <Drawer open={open !== undefined} onClose={() => (busy ? undefined : close())} labelledBy="place-feedback-drawer-title">
        {open !== undefined && (
          <>
            <DrawerHeader
              titleId="place-feedback-drawer-title"
              title={drawerTitle(open)}
              subtitle={<Badge tone={statusTone(open.status)}>{v('place.feedback_status', open.status)}</Badge>}
              onClose={close}
              closeLabel={t('ui.close')}
            />
            <DrawerBody>
              <DrawerStack>
                {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}
                {open.kind !== 'comment' && (
                  <Stack gap={2}>
                    <SectionTitle>{t('places.section.map')}</SectionTitle>
                    <PlaceMap point={open.location ?? open.targetLocation ?? undefined} pickup={null} tiles={map.tiles} center={map.center} />
                  </Stack>
                )}
                <Stack gap={1}>
                  <SectionTitle>{t('places.feedback.section.rider')}</SectionTitle>
                  <DetailRow label={t('places.feedback.field.kind')} value={v('place.feedback_kind', open.kind)} mono={false} />
                  {open.issue !== null && <DetailRow label={t('places.feedback.field.issue')} value={v('place.feedback_issue', open.issue)} mono={false} />}
                  {open.targetLabel !== null && <DetailRow label={t('places.feedback.field.about')} value={open.targetLabel} mono={false} />}
                  {open.query !== null && <DetailRow label={t('places.feedback.field.query')} value={open.query} mono={false} />}
                  {open.kind !== 'comment' && (
                    <>
                      <DetailRow label={t('places.feedback.field.name')} value={open.name ?? '—'} mono={false} />
                      <DetailRow label={t('places.feedback.field.address')} value={open.address ?? '—'} mono={false} />
                      <DetailRow label={t('places.feedback.field.point')} value={open.location === null ? '—' : `${formatCoordinate(open.location.latitude)}, ${formatCoordinate(open.location.longitude)}`} mono />
                    </>
                  )}
                  <DetailRow label={t('places.feedback.field.note')} value={open.note ?? '—'} mono={false} />
                  <DetailRow label={t('places.feedback.col.city')} value={placeNames.market(open.marketId)} mono={false} />
                  <DetailRow label={t('places.feedback.field.received')} value={when(open.createdAt, open.marketId)} mono />
                </Stack>
                {open.kind === 'fix' && (
                  <Stack gap={1}>
                    <SectionTitle>{t('places.feedback.section.base')}</SectionTitle>
                    {openTarget === undefined ? (
                      <Text tone="secondary">{open.targetPlaceId === null ? t('places.feedback.base.none') : t('places.feedback.base.gone')}</Text>
                    ) : (
                      <>
                        <DetailRow label={t('places.feedback.field.name')} value={openTarget.name ?? '—'} mono={false} />
                        <DetailRow label={t('places.feedback.field.address')} value={openTarget.formattedAddress} mono={false} />
                        <DetailRow label={t('places.field.source')} value={v('place.source', openTarget.source)} mono={false} />
                        <DetailRow label={t('places.field.verified')}>
                          <Badge tone={openTarget.verified ? 'success' : 'neutral'}>{t(openTarget.verified ? 'places.feedback.base.verified' : 'places.filter.no')}</Badge>
                        </DetailRow>
                        <DetailRow label={t('places.feedback.field.place')}><IdRef value={openTarget.id} copy /></DetailRow>
                      </>
                    )}
                  </Stack>
                )}
                {open.status !== 'pending' && (
                  <Stack gap={1}>
                    <SectionTitle>{t('places.feedback.section.decision')}</SectionTitle>
                    {open.rejectReason !== null && <DetailRow label={t('places.feedback.field.reason')} value={v('place.feedback_reject_reason', open.rejectReason)} mono={false} />}
                    {open.resolvedPlaceId !== null && <DetailRow label={t('places.feedback.field.place')}><IdRef value={open.resolvedPlaceId} copy /></DetailRow>}
                    <DetailRow
                      label={t('places.feedback.field.decidedBy')}
                      value={t('places.field.by', { when: open.reviewedAt === null ? '—' : when(open.reviewedAt, open.marketId), who: open.reviewedBy === null ? t('places.operator.unknown') : data?.operators[open.reviewedBy] ?? t('places.operator.unknown') })}
                      mono={false}
                    />
                  </Stack>
                )}
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={close}>{t('places.drawer.close')}</Button>
              {authority.mayManage && actions !== undefined && (
                <>
                  {actions.reject && <Button variant="secondary" disabled={busy} onClick={openReject}>{t('places.feedback.reject')}</Button>}
                  {actions.merge && (
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => {
                        setMergeQ(open.name ?? open.targetLabel ?? '');
                        setMergeHits(undefined);
                        setMergeInto('');
                        setMergeOverride(false);
                        setMerging(true);
                      }}
                    >
                      {t('places.feedback.merge')}
                    </Button>
                  )}
                  {actions.accept && <Button variant="primary" disabled={busy} onClick={() => setAccepting(acceptDraftOf(open))}>{t('places.feedback.accept')}</Button>}
                  {actions.handle && <Button variant="primary" disabled={busy} onClick={() => void runHandle()}>{t('places.feedback.handle')}</Button>}
                </>
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>

      <FormDialog open={accepting !== undefined && open !== undefined} id="place-feedback-accept" icon="check" tone="neutral" title={t('places.feedback.accept.title')} body={acceptBody} busy={busy} onCancel={() => setAccepting(undefined)}>
        {accepting !== undefined && open !== undefined && (
          <Stack gap={3}>
            {(open.kind === 'add' || open.targetPlaceId === null || open.issue === 'wrong_name' || open.issue === 'other') && (
              <Field label={t('places.feedback.field.name')} htmlFor="place-feedback-accept-name">
                <TextInput id="place-feedback-accept-name" value={accepting.name} onChange={(e) => setAccepting({ ...accepting, name: e.target.value })} />
              </Field>
            )}
            {(open.kind === 'add' || open.targetPlaceId === null || open.issue === 'wrong_address' || open.issue === 'other') && (
              <Field label={t('places.feedback.field.address')} htmlFor="place-feedback-accept-address">
                <TextInput id="place-feedback-accept-address" value={accepting.address} onChange={(e) => setAccepting({ ...accepting, address: e.target.value })} />
              </Field>
            )}
            {acceptCreatesPlace(open) && open.issue !== 'closed' && (
              <Field label={t('places.feedback.accept.kind')} htmlFor="place-feedback-accept-kind">
                <Select id="place-feedback-accept-kind" value={accepting.kind} onChange={(e) => setAccepting({ ...accepting, kind: e.target.value })}>
                  <option value="">{t('places.feedback.accept.kindNone')}</option>
                  {kinds.map((k) => (
                    <option key={k} value={k}>{v('place.kind', k)}</option>
                  ))}
                </Select>
              </Field>
            )}
            {showAcceptPoint && (
              <Stack gap={2}>
                <Text role="caption" tone="secondary">{t('places.feedback.accept.mapHint')}</Text>
                <PlaceMap point={acceptPoint} pickup={null} tiles={map.tiles} center={map.center} onTap={(p) => setAccepting(withAcceptPoint(accepting, p))} />
                <Row gap={2}>
                  <Field label={t('places.feedback.accept.lat')} htmlFor="place-feedback-accept-lat">
                    <TextInput id="place-feedback-accept-lat" className="tnum" value={accepting.latitude} onChange={(e) => setAccepting({ ...accepting, latitude: e.target.value })} />
                  </Field>
                  <Field label={t('places.feedback.accept.lng')} htmlFor="place-feedback-accept-lng">
                    <TextInput id="place-feedback-accept-lng" className="tnum" value={accepting.longitude} onChange={(e) => setAccepting({ ...accepting, longitude: e.target.value })} />
                  </Field>
                </Row>
              </Stack>
            )}
            {acceptNeedsOverride(open, places) && <Checkbox checked={accepting.overrideVerified} onChange={(overrideVerified) => setAccepting({ ...accepting, overrideVerified })} label={t('places.feedback.accept.override')} />}
            {acceptIssue !== undefined && <Text role="caption" tone="secondary" aria-live="polite">{v('problem.place_feedback', acceptIssue)}</Text>}
            {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}
            <Row gap={2} justify="end">
              <Button variant="ghost" disabled={busy} onClick={() => setAccepting(undefined)}>{t('ui.cancel')}</Button>
              <Button variant="primary" disabled={busy || acceptIssue !== undefined} onClick={() => void runAccept()}>{t('places.feedback.accept.confirm')}</Button>
            </Row>
          </Stack>
        )}
      </FormDialog>

      <FormDialog open={merging && open !== undefined} id="place-feedback-merge" icon="mapPin" tone="neutral" title={t('places.feedback.merge.title')} body={t('places.feedback.merge.body')} busy={busy} onCancel={() => setMerging(false)}>
        {merging && open !== undefined && (
          <Stack gap={3}>
            <Row gap={2} align="end">
              <Field label={t('places.feedback.merge.search')} htmlFor="place-feedback-merge-q">
                <TextInput id="place-feedback-merge-q" value={mergeQ} placeholder={t('places.feedback.merge.searchPh')} onChange={(e) => setMergeQ(e.target.value)} onKeyDown={(e) => (e.key === 'Enter' ? void searchMerge() : undefined)} />
              </Field>
              <Button variant="secondary" disabled={mergeQ.trim() === ''} onClick={() => void searchMerge()}>{t('places.feedback.merge.find')}</Button>
            </Row>
            {mergeHits !== undefined && mergeHits.length === 0 && <Text tone="secondary">{t('places.feedback.merge.none')}</Text>}
            {mergeHits !== undefined && mergeHits.length > 0 && (
              <RadioGroup
                name="place-feedback-merge-into"
                ariaLabel={t('places.feedback.merge.search')}
                value={mergeInto}
                onChange={setMergeInto}
                options={mergeHits.map((p) => ({ value: p.id, label: p.name !== null ? `${p.name} · ${p.formattedAddress}` : p.formattedAddress }))}
              />
            )}
            {mergeTarget?.verified === true && <Checkbox checked={mergeOverride} onChange={setMergeOverride} label={t('places.feedback.merge.override')} />}
            {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}
            <Row gap={2} justify="end">
              <Button variant="ghost" disabled={busy} onClick={() => setMerging(false)}>{t('ui.cancel')}</Button>
              <Button variant="primary" disabled={busy || mergeInto === ''} onClick={() => void runMerge()}>{t('places.feedback.merge.confirm')}</Button>
            </Row>
          </Stack>
        )}
      </FormDialog>

      <FormDialog open={rejectOpen && open !== undefined} id="place-feedback-reject" icon="ban" tone="danger" title={t('places.feedback.reject.title')} body={t('places.feedback.reject.body')} busy={busy} onCancel={() => setRejectOpen(false)}>
        {rejectOpen && (
          <Stack gap={3}>
            <RadioGroup
              name="place-feedback-reject-reason"
              ariaLabel={t('places.feedback.reject.reasons')}
              value={rejectReason ?? ''}
              onChange={(r) => setRejectReason(r as PlaceFeedbackRejectReasonView)}
              options={FEEDBACK_REJECT_REASONS.map((r) => ({ value: r, label: v('place.feedback_reject_reason', r) }))}
            />
            {rejectReason === undefined && <Text role="caption" tone="secondary" aria-live="polite">{v('problem.place_feedback', 'PLACE_FEEDBACK_REJECT_REASON_INVALID')}</Text>}
            {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}
            <Row gap={2} justify="end">
              <Button variant="ghost" disabled={busy} onClick={() => setRejectOpen(false)}>{t('ui.cancel')}</Button>
              <Button variant="danger-solid" disabled={busy || rejectReason === undefined} onClick={() => void runReject()}>{t('places.feedback.reject.confirm')}</Button>
            </Row>
          </Stack>
        )}
      </FormDialog>
    </Stack>
  );
}
